/* eslint-disable node/no-unpublished-import */
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ButtonComponent } from '@ansys-internal/awc-angular/buttons';
import { GridComponent } from '@ansys-internal/awc-angular/layouts';
import { SharedModule } from './shared/shared.module';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  LabelComponent,
  SwitchComponent,
} from '@ansys-internal/awc-angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ChipComponent } from '@ansys-internal/awc-angular/chips';
import { ButtonGroupComponent } from '@ansys-internal/awc-angular/buttons';
import { TreeviewComponent } from './components/widgets/treeview/treeview.component';

import { ConceptLayoutComponent } from './components/layout/concept-layout/concept-layout.component';
import {
  DashboardModule,
  DashboardPanelComponent,
  TextareaComponent,
} from '@ansys-internal/awf-angular/dashboard';
import { ActiveSelectionEditInputsComponent } from './components/widgets/active-selection-edit-inputs/active-selection-edit-inputs.component';
import { GraphDisplayComponent } from './components/widgets/charts/graph-display/graph-display.component';
import { TableDisplayComponent } from './components/widgets/charts/table-display/table-display.component';
import { MotorArchitectureComponent } from './components/widgets/motor-architecture/motor-architecture.component';
import { DataDisplayPanelComponent } from './components/layout/data-display-panel/data-display-panel.component';
import { SelectComponent } from '@ansys-internal/awc-angular/forms';
import { PartInputsComponent } from './components/widgets/part-inputs/part-inputs.component';
import { NoDataDisplayComponent } from './components/widgets/no-data-display/no-data-display.component';
import { ConceptInputComponent } from './components/widgets/forms/input/concept-input.component';
import { ProgressBarComponent } from '@ansys-internal/awc-angular/progress-bar';
import { SessionModule } from '@ansys-internal/awf-angular/auth';
import {
  ContributionsTableComponent,
  PageRouteComponent, PRODUCTS_DATA_PROVIDER,
} from '@ansys-internal/awf-angular/shared';
import { UserSocketService } from '@ansys-internal/awf-angular/sockets';
import { ConfirmComponent } from './components/dialogs/confirm/confirm.component';
import { ResultsListComponent } from './components/widgets/results-list/results-list.component';
import { StartSolveComponent } from './components/dialogs/start-solve/start-solve.component';
import { ActiveJobDisplayComponent } from './components/widgets/active-job-display/active-job-display.component';
import { AccordionComponent } from '@ansys-internal/awc-angular/accordions';
import { NoResultsComponent } from '@ansys-internal/awf-angular/dashboard';
import { ChartComponent } from '@ansys-internal/awc-angular/charts';
import { TableComponent } from '@ansys-internal/awc-angular/tables';
import { SectionCardComponent } from './components/layout/section-card/section-card.component';
import { ScientificNotationPipe } from './shared/pipes/scientific-notation/scientific-notation.pipe';
import { DatePipe } from '@angular/common';
import { UnitsComponent } from './components/dialogs/units/units.component';
import { BlueprintDisplayComponent } from './components/widgets/charts/blueprint-display/blueprint-display.component';
import { MarkerDisplayComponent } from './components/widgets/charts/blueprint-display/marker-display/marker-display.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { LayoutService } from './shared/services/layout.service';
import { UnitFromTypePipe } from './shared/pipes/unit-from-type/unit-from-type.pipe';
import { AerodynamicsComponent } from './components/widgets/part-inputs/inputs/aerodynamics/aerodynamics.component';
import { MassComponent } from './components/widgets/part-inputs/inputs/mass/mass.component';
import { WheelsComponent } from './components/widgets/part-inputs/inputs/wheels/wheels.component';
import { ClutchComponent } from './components/widgets/part-inputs/inputs/clutch/clutch.component';
import { BatteryComponent } from './components/widgets/part-inputs/inputs/battery/battery.component';
import { TransmissionComponent } from './components/widgets/part-inputs/inputs/transmission/transmission.component';
import { InverterComponent } from './components/widgets/part-inputs/inputs/inverter/inverter.component';
import { StaticRequirementsComponent } from './components/widgets/part-inputs/inputs/static-requirements/static-requirements.component';
import { DynamicRequirementsComponent } from './components/widgets/part-inputs/inputs/dynamic-requirements/dynamic-requirements.component';
import { DriveCyclesComponent } from './components/widgets/part-inputs/inputs/drive-cycles/drive-cycles.component';
import { DataConfigComponent } from './components/widgets/data-config/data-config.component';
import { ArchitectureConfigurationsComponent } from './components/widgets/architecture-configurations/architecture-configurations.component';
import { LibraryPanelComponent } from './components/widgets/library-panel/library-panel.component';
import { LibraryExportComponent } from './components/dialogs/library-export/library-export.component';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { ImportLibraryAssetComponent } from './components/dialogs/import-library-asset/import-library-asset.component';
import { DownloadDatasetComponent } from './components/dialogs/download-dataset/download-dataset.component';
import { AndromedaModule } from './andromeda.module';
import { MotorComponent } from './components/widgets/part-inputs/inputs/motor/motor.component';
import { ArchitectureDataPanelComponent } from './components/widgets/architecture-data-panel/architecture-data-panel.component';
import { ImportDriveCycleComponent } from './components/dialogs/import-drive-cycle/import-drive-cycle.component';
import { ErrorHandlerInterceptor } from './shared/services/error-handler-interceptor.service';
import { DesignApiService } from '@ansys/cloud-angular-client';
import { provideActions } from '@ansys-internal/awf-angular/providers';
import { FeatureFlagModule } from './shared/modules/feature-flag.module';
import { CreateProjectComponent } from './components/dialogs/create-project/create-project.component';
import { SwitchViewComponent } from './components/widgets/charts/ui/switch-view/switch-view.component';
import { LeftSectionComponent } from './components/layout/left-section/left-section.component';
import { LoadingContentScreenComponent } from './components/layout/loading-content-screen/loading-content-screen.component';
import { RightSectionComponent } from './components/layout/right-section/right-section.component';
import { DataDisplayComparisonToolComponent } from './components/layout/data-display-comparison-tool/data-display-comparison-tool.component';
import { provideDashboardDefaults } from '@ansys-internal/awf-data-providers';
import { LoadingSkeletonComponent } from "@ansys-internal/awc-angular/loading-skeleton";
import { HttpLink } from '@apollo/client/link/http';
import {ProductDataDefaultProvider} from "./shared/providers/products-data-provider";

@NgModule({
  declarations: [AppComponent],
  imports: [
    PageRouteComponent,
    AndromedaModule,
    ConceptLayoutComponent,
    TableDisplayComponent,
    DataDisplayPanelComponent,
    NoDataDisplayComponent,
    ConfirmComponent,
    UnitsComponent,
    BlueprintDisplayComponent,
    LibraryExportComponent,
    DownloadDatasetComponent,
    ImportLibraryAssetComponent,
    LibraryPanelComponent,
    ImportDriveCycleComponent,
    LibraryExportComponent,
    DownloadDatasetComponent,
    ImportLibraryAssetComponent,
    LibraryPanelComponent,
    ImportDriveCycleComponent,
    GraphDisplayComponent,
    ActiveSelectionEditInputsComponent,
    MarkerDisplayComponent,
    AerodynamicsComponent,
    MassComponent,
    WheelsComponent,
    ClutchComponent,
    TransmissionComponent,
    MotorComponent,
    InverterComponent,
    StaticRequirementsComponent,
    DynamicRequirementsComponent,
    DriveCyclesComponent,
    ArchitectureConfigurationsComponent,
    ArchitectureDataPanelComponent,
    BatteryComponent,
    ConceptInputComponent,
    ResultsListComponent,
    SharedModule,
    BrowserModule,
    AppRoutingModule,
    SwitchComponent,
    GridComponent,
    ButtonComponent,
    SessionModule,
    BrowserAnimationsModule,
    ChipComponent,
    ButtonGroupComponent,
    StartSolveComponent,
    SelectComponent,
    DashboardPanelComponent,
    DashboardModule,
    LoadingSkeletonComponent,
    AccordionComponent,
    NoResultsComponent,
    ChartComponent,
    DragDropModule,
    TableComponent,
    TextareaComponent,
    ProgressBarComponent,
    ScrollingModule,
    LabelComponent,
    ContributionsTableComponent,
    MotorArchitectureComponent,
    DataConfigComponent,
    MarkerDisplayComponent,
    GraphDisplayComponent,
    FeatureFlagModule,
    ArchitectureConfigurationsComponent,
    SectionCardComponent,
    TreeviewComponent,
    LeftSectionComponent,
    LoadingContentScreenComponent,
    CreateProjectComponent,
    SwitchViewComponent,
    RightSectionComponent,
    ActiveSelectionEditInputsComponent,
    ActiveJobDisplayComponent,
    PartInputsComponent,
    AerodynamicsComponent,
    MassComponent,
    WheelsComponent,
    ClutchComponent,
    TransmissionComponent,
    InverterComponent,
    StaticRequirementsComponent,
    DynamicRequirementsComponent,
    DriveCyclesComponent,
    MotorComponent,
    RightSectionComponent,
    ActiveSelectionEditInputsComponent,
    ActiveJobDisplayComponent,
    PartInputsComponent,
    AerodynamicsComponent,
    MassComponent,
    WheelsComponent,
    ClutchComponent,
    TransmissionComponent,
    MotorComponent,
    InverterComponent,
    StaticRequirementsComponent,
    DynamicRequirementsComponent,
    DriveCyclesComponent,
    MotorComponent,
    DataDisplayComparisonToolComponent,
    PageRouteComponent,
  ],
  providers: [
    HttpLink,
    provideActions(),
    provideDashboardDefaults(),
    {
      provide: PRODUCTS_DATA_PROVIDER,
      useClass: ProductDataDefaultProvider,
    },
    provideHttpClient(withInterceptorsFromDi()),
    UserSocketService,
    DatePipe,
    ScientificNotationPipe,
    UnitFromTypePipe,
    LayoutService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlerInterceptor,
      multi: true,
    },
    DesignApiService,
  ],
  bootstrap: [AppComponent],
  exports: [
    TreeviewComponent,
    ActiveJobDisplayComponent,
    ActiveSelectionEditInputsComponent,
    PartInputsComponent,
    DriveCyclesComponent,
    DynamicRequirementsComponent,
    StaticRequirementsComponent,
    InverterComponent,
    MotorComponent,
    TransmissionComponent,
    ClutchComponent,
    WheelsComponent,
    MassComponent,
    AerodynamicsComponent,
    ConfirmComponent,
  ],
})
export class AppModule {}
